import {
	ChangeEvent,
	FormEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import Container from '@/components/Container';
import HeadingOne from '@/components/Heading/HeadingOne';
import ModalDocuments from '@/components/ModalDocuments';
import PageHead from '@/components/PageHead';
import PageLayout from '@/components/layout/page-layout';
import Alert, { AlertProps } from '@/components/Alert';

import NextImage from 'next/image';
import Portal from '@/components/Portal';

const maxBirthToDonor = (() => {
	let date = new Date();
	let year = date.getFullYear() - 2;
	let month: number | string = date.getMonth();
	let day: number | string = date.getDate();

	month = month < 10 ? `0${month}` : month;
	day = day < 10 ? `0${day}` : day;

	return `${year}-${month}-${day}`;
})();

type DonorCardDataProps = {
	fullName?: string;
	dateOfBirth?: string;
	bloodType?: 'A+' | 'A-' | 'B+' | 'B-' | 'AB+' | 'AB-' | 'O+' | 'O-';
	email?: string;
	terms_policy?: boolean;
	newsletter?: boolean;
};
export default function Page() {
	const [submitIsPermitted, setSubmitIsPermitted] = useState(false);
	const [isOpenTermos, setIsOpenTermos] = useState(false);
	const [donorCardData, setDonorCardData] = useState({} as DonorCardDataProps);
	const [alertState, setAlertState] = useState({} as AlertProps);

	const formRef = useRef<HTMLFormElement>();

	useEffect(() => {
		if (
			donorCardData.terms_policy == true &&
			donorCardData.bloodType.length > 0 &&
			donorCardData.dateOfBirth.length > 0 &&
			donorCardData.email.length > 0 &&
			donorCardData.fullName.length > 0
		) {
			setSubmitIsPermitted(true);
		} else setSubmitIsPermitted(false);
	}, [donorCardData]);

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		let target = event.target;
		let name = target.name;
		let value: string | boolean = target.value;

		if (name === 'dateOfBirth') value = value.split('-').reverse().join('-');

		if (['terms_policy', 'newsletter'].includes(name)) {
			value = target.checked;
		}

		setDonorCardData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
		// let data = {} as DonorCardDataProps;
		// for(let [key, value] of formData) {
		// 	data[key] = value
		// }

		event.preventDefault();
		event.stopPropagation();

		setSubmitIsPermitted(false);

		let formData = new FormData(event.currentTarget);
		let data = {} as DonorCardDataProps;
		data = donorCardData;

		if(!data.newsletter) {
			data['newsletter'] = false
		}

		const response = await fetch('/api/v1/public/donor-card', {
			method: 'POST',
			body: JSON.stringify(data),
		});

		console.log(response.status);

		if (response) {
			const result = await response.json();
			setAlertState((prev) => ({
				isOpen: true,
				message: result.body.message,
				secondsToClose: 5000,
			}));

			setTimeout(() => {
				setAlertState((prev) => ({
					isOpen: false,
					message: '',
					secondsToClose: 5000,
				}));
			}, 5000);

			formRef.current.reset();

			setTimeout(() => {
				window.open(
					`/cartao-doador/print?fullName=${data.fullName}&dateOfBirth=${data.dateOfBirth}&bloodType=${data.bloodType}`
				);
			}, 5000);
		}
	};

	return (
		<>
			<PageHead
				page={{
					title: 'Instituto Deixe Vivo | Cartão Doador de órgãos e tecidos',
					keywords:
						'OSC, Instituto, Deixe Vivo, Doação de órgãos e tecidos, cartão doador, declarar doador, declare-se doador de órgãos e tecidos, avise sua família',
					colorScheme: 'light dark',
					description:
						'O Cartão Doador de Órgãos e Tecidos é uma forma simbólica de incentivar a doação',
					robots: 'follow, index',
				}}
				og={{
					description:
						'O Cartão Doador de Órgãos e Tecidos é uma forma simbólica de incentivar a doação',
					site_name: 'Instituto Deixe Vivo | Cartão Doador de órgãos e tecidos',
					title: 'Instituto Deixe Vivo | Cartão Doador de órgãos e tecidos',
					type: 'website',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						fbAdmins: 'instituto.deixevivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
						type: 'image/jpg',
						width: '1200',
						height: '1200',
					},
				}}
				twitter={{
					card: 'summary',
					description:
						'O Cartão Doador de Órgãos e Tecidos é uma forma simbólica de incentivar a doação',
					site: 'Instituto Deixe Vivo | Cartão Doador de órgãos e tecidos',
					title: 'Instituto Deixe Vivo | Cartão Doador de órgãos e tecidos',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
					},
				}}
			/>

			<PageLayout>
				<Container className="py-8">
					<HeadingOne horizontalRow>
						Cartão Doador de Órgãos e Tecidos
					</HeadingOne>

					<div className="mt-8">
						<section>
							<p>
								O Cartão Doador de Órgãos e Tecidos é uma forma simbólica de
								incentivar a doação.
							</p>

							<p>
								Atualmente, no Brasil, a única forma de se tornar doador(a) é
								avisando sua família sobre sua vontade, pois em caso de morte
								encefálica, só eles poderão dizer{' '}
								<span className="uppercase">{'sim '}</span>
								por você!
							</p>
						</section>
					</div>

					<div className="mt-8 flex flex-col lg:flex-row">
						<section className="flex-1 mb-16">
							<form
								ref={formRef}
								id="form-cartao-doador"
								onSubmit={handleFormSubmit}
							>
								<h2 className="mb-4">
									Formulário de preenchimento para gerar o cartão
								</h2>

								<fieldset className="border-2 p-4">
									<legend>
										<span>Dados Pessoais</span>
									</legend>

									<div className="mb-2">
										<label className="block mr-2 mb-1" htmlFor="fullName">
											<span>Nome: </span>
										</label>
										<input
											required
											className="block p-1 w-full border-b-2 border-b-zinc-300 text-black dark:text-white"
											id="fullName"
											type="text"
											name="fullName"
											minLength={5}
											onChange={handleInputChange}
										/>
									</div>

									<div className="mb-2">
										<label className="block mr-2 mb-1" htmlFor="dateOfBirth">
											<span>Data de Nascimento:</span>
										</label>
										<input
											required
											className="block p-1 w-full border-b-2 border-b-zinc-300 text-black dark:text-white"
											id="dateOfBirth"
											type="date"
											name="dateOfBirth"
											min="1933-01-01"
											max={maxBirthToDonor}
											onChange={handleInputChange}
										/>
									</div>

									<div className="mb-2">
										<label className="block mr-2 mb-1" htmlFor="bloodType">
											<span>Tipo Sanguíneo:</span>
										</label>
										<input
											required
											className="block p-1 w-full border-b-2 border-b-zinc-300 text-black dark:text-white"
											id="bloodType"
											name="bloodType"
											list="bloodType-list"
											onChange={handleInputChange}
											type="text"
										/>
										<datalist id="bloodType-list">
											<optgroup label="A">
												<option value="A positivo">A+</option>
												<option value="A negativo">A-</option>
											</optgroup>
											<optgroup label="B">
												<option value="B positivo">B+</option>
												<option value="B negativo">B-</option>
											</optgroup>
											<optgroup label="AB">
												<option label="AB+" value="AB positivo">
													AB+
												</option>
												<option value="AB negativo">AB-</option>
											</optgroup>
											<optgroup label="O">
												<option value="O positivo">O+</option>
												<option value="O negativo">O-</option>
											</optgroup>
										</datalist>
									</div>

									<div className="mb-2">
										<label className="block mr-2 mb-1" htmlFor="email">
											<span>E-mail: </span>
										</label>
										<input
											required
											className="block p-1 w-full border-b-2 border-b-zinc-300 text-black dark:text-white"
											id="email"
											type="email"
											name="email"
											onChange={handleInputChange}
											minLength={10}
										/>
									</div>
								</fieldset>

								<fieldset className="border-2 p-4">
									<legend>
										<span>Aceites</span>
									</legend>

									<div>
										<input
											required
											id="terms_policy"
											type="checkbox"
											name="terms_policy"
											onChange={handleInputChange}
										/>
										<label htmlFor="terms_policy" className="ml-2">
											Aceito os{' '}
											<span
												className="text-blue-500 cursor-pointer"
												onClick={(ev) => setIsOpenTermos(true)}
											>
												termos de serviços e política de privacidade
											</span>
										</label>
									</div>

									<div>
										<input
											id="newsletter"
											type="checkbox"
											name="newsletter"
											onChange={handleInputChange}
										/>
										<label htmlFor="newsletter" className="ml-2">
											Aceito receber emails sobre as ações e novidades do
											Instituto
										</label>
									</div>
								</fieldset>

								<div className="mt-4">
									<button
										disabled={!submitIsPermitted}
										formTarget="#form-cartao-doador"
										type="submit"
										className="button rounded font-bold uppercase bg-primary-dark cursor-pointer"
									>
										Gerar Meu Cartão
									</button>
								</div>
							</form>
						</section>

						<section className="w-full lg:w-[50%] flex flex-col items-center lg:items-end">
							<figure className="mb-2">
								<picture>
									<source
										srcSet="/assets/images/illustrations/cartao-doador/cartao-frente.svg"
										media="(min-width: 0px)"
									/>
									<NextImage
										alt="Frente do cartão doador"
										src="/assets/images/illustrations/cartao-doador/cartao-frente.svg"
										width={339}
										height={227}
									/>
								</picture>
								<figcaption className="text-center text-sm">
									Frente do cartão doador
								</figcaption>
							</figure>
							<div className="relative">
								<figure>
									<picture>
										<source
											srcSet="/assets/images/illustrations/cartao-doador/cartao-verso.svg"
											media="(min-width: 0px)"
										/>
										<NextImage
											alt="Verso do cartão doador"
											src="/assets/images/illustrations/cartao-doador/cartao-verso.svg"
											width={339}
											height={227}
										/>
									</picture>
									<figcaption className="text-center text-sm">
										Verso do cartão doador
									</figcaption>
								</figure>
								<div className="select-none absolute text-black top-[28%] left-[18%]">
									<span>{donorCardData.fullName}</span>
								</div>
								<div className="select-none absolute text-black top-[40%] left-[36%]">
									<span>{donorCardData.dateOfBirth}</span>
								</div>
								<div className="select-none absolute text-black top-[55%] left-[28%]">
									<span>{donorCardData.bloodType}</span>
								</div>
							</div>
						</section>
					</div>
				</Container>
			</PageLayout>

			{isOpenTermos && (
				<Portal>
					<ModalDocuments
						isOpen={isOpenTermos}
						setIsOpen={setIsOpenTermos}
						src="/assets/docs/politica-privacidade-e-termos-de-servicos.pdf"
						title="Política de Privacidade e Termos de Serviços"
					/>
				</Portal>
			)}

			<Portal>
				<Alert {...alertState} />
			</Portal>
		</>
	);
}
